import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { getCrossID, translateFollowingItemForWeb, translateExtraBoolean } from 'utils/filters';
import { RANGE_FIELDS } from '../constants';

/**
 * Custom hook for managing filter values and formatting
 *
 * This hook handles the complex logic of formatting and managing filter values
 * for the NewEntityFilters component. It processes both regular filters and
 * cross-entity filters, handling various data types including dates, probabilities,
 * and ranges with appropriate formatting for display and storage.
 *
 * @param {Object} filters - The main filters object containing filter configurations
 * @param {Object} crossFilters - Cross-entity filters for related entities
 * @param {Object} state - The filter state containing mappedSchema and crossSchema
 * @param {Object} entity - The primary entity being filtered
 * @param {boolean} hasCrossFilters - Flag indicating if cross filters are enabled
 * @param {Function} getCrossValues - Function to retrieve cross-entity filter values
 * @param {Array<string>} filtersToIgnore - Array of filter keys to exclude from processing
 *
 * @returns {Object} Object containing:
 *   - values: Formatted filter values ready for display
 *   - formatRangeValueToSave: Function to format range values for storage
 */
const useFilterValues = (
    filters,
    crossFilters,
    state,
    entity,
    hasCrossFilters,
    getCrossValues,
    filtersToIgnore,
) => {
    /**
     * Converts probability values from internal format (0-1) to display format (0-10)
     * Used for showing probability ranges in the UI
     *
     * @param {Object} newValue - Object with 'from' and 'to' probability values (0-1 range)
     * @param {number} newValue.from - Starting probability value
     * @param {number} newValue.to - Ending probability value
     * @returns {Array<number>} Array with scaled values [from*10, to*10]
     */
    const formatProbabilityToShow = useCallback((newValue) => {
        return [newValue.from * 10, newValue.to * 10];
    }, []);

    /**
     * Converts probability values from display format (0-10) to internal format (0-1)
     * Used for saving probability ranges from the UI
     *
     * @param {Object} newValue - Object with 'from' and 'to' probability values (0-10 range)
     * @param {number} [newValue.from] - Starting probability value
     * @param {number} [newValue.to] - Ending probability value
     * @returns {Object} Object with scaled values { from: value/10, to: value/10 }
     */
    const formatProbabilityToSave = useCallback((newValue) => {
        return { from: newValue?.from / 10 || 0, to: newValue?.to / 10 || 0 };
    }, []);

    /**
     * Formats range values for display in the UI
     * Handles different data types (probability, date, numeric) and converts them
     * to array format suitable for range input components
     *
     * @param {Object|Array} value - The range value to format
     * @param {Object} field - Field configuration object containing dataType and other properties
     * @param {string} field.dataType - Type of data ('probability', 'date', or other)
     * @returns {Array} Formatted range array [from, to] or empty array if invalid
     */
    const formatRangeValueToShow = useCallback(
        (value, field) => {
            if (!value || !field) return [];
            if (Array.isArray(value)) return value;

            let formattedValue;
            if (field.dataType === 'probability') {
                formattedValue = formatProbabilityToShow(value);
            } else if (field.dataType === 'date') {
                const from = value['from'];
                const to = value['to'];
                formattedValue = [
                    from ? moment.utc(from).local().toDate() : '',
                    to ? moment.utc(to).local().toDate() : '',
                ];
            } else {
                formattedValue = [value['from'], value['to']];
            }
            return formattedValue;
        },
        [formatProbabilityToShow],
    );

    /**
     * Formats range values for saving/storage
     * Converts array format [from, to] back to object format { from, to }
     * with appropriate defaults and special handling for probability data
     *
     * @param {Array} value - Array with range values [from, to]
     * @param {Object} field - Field configuration object
     * @param {Object} [field.inputProps] - Input properties containing min/max values
     * @param {number} [field.inputProps.min] - Minimum allowed value
     * @param {number} [field.inputProps.max] - Maximum allowed value
     * @param {string} field.dataType - Type of data ('probability' or other)
     * @returns {Object} Formatted range object { from, to }
     */
    const formatRangeValueToSave = useCallback(
        (value, field) => {
            let defaultFrom = '';
            let defaultTo = '';

            // Set default values from field configuration
            if (field?.inputProps?.min || !isNaN(field?.inputProps?.min)) {
                defaultFrom = field.inputProps.min;
            }
            if (field?.inputProps?.max || !isNaN(field?.inputProps?.max)) {
                defaultTo = field.inputProps.max;
            }

            let formattedValue = {
                from: value?.[0] || defaultFrom,
                to: value?.[1] || defaultTo,
            };

            // Apply special formatting for probability fields
            if (field.dataType === 'probability') {
                formattedValue = formatProbabilityToSave(formattedValue);
            }

            return formattedValue;
        },
        [formatProbabilityToSave],
    );

    /**
     * Extracts and formats filter values from the filters object
     * Processes filters by translating them for web display, applying range formatting,
     * and creating cross-entity keys when needed
     *
     * @param {Object} params - Parameters object
     * @param {Object} params.filters - The filters object to process
     * @param {Object} params.entity - The primary entity being filtered
     * @param {Object} params.mappedSchema - Schema mapping for field configurations
     * @param {Object} [params.crossEntity] - Cross-entity information if applicable
     * @param {string} [params.crossEntity.entity] - Cross-entity name
     * @returns {Object} Object with formatted filter values keyed by filter name
     */
    const getValues = useCallback(
        ({ filters, entity, mappedSchema, crossEntity }) => {
            const finalEntity = crossEntity || entity;
            let newFilters = translateFollowingItemForWeb(filters, finalEntity);

            const newValues = Object.keys(newFilters).reduce((obj, key) => {
                // Skip filters that should be ignored
                if (filtersToIgnore.includes(key)) return obj;

                let newFilter = filters[key];
                newFilter = translateExtraBoolean(newFilter, finalEntity);

                // Get the filter value (prefer completeValues over value)
                let value = newFilter.completeValues || newFilter.value;

                // Apply range formatting for range field types
                if (RANGE_FIELDS.includes(newFilter.dataType)) {
                    const field = mappedSchema[key] || null;
                    value = formatRangeValueToShow(value, field);
                }

                // Create the final key (with cross-entity prefix if needed)
                const finalKey = getCrossID(key, crossEntity?.entity);
                obj[finalKey] = value;
                return obj;
            }, {});

            return newValues;
        },
        [filtersToIgnore, formatRangeValueToShow],
    );

    /**
     * Computed values for all filters (main + cross-entity)
     * Memoized to prevent unnecessary recalculations when dependencies haven't changed
     *
     * Returns an empty object if:
     * - mappedSchema is not available
     * - No cross filters and no main filters exist
     *
     * Combines main entity filter values with cross-entity filter values when applicable
     */
    const values = useMemo(() => {
        // Early returns for invalid states
        if (!state.mappedSchema) return {};
        if (!hasCrossFilters && (!filters || Object.entries(filters).length === 0)) return {};

        // Get values for main entity filters
        let finalValues = getValues({ filters, entity, mappedSchema: state.mappedSchema });

        // Merge in cross-entity filter values if they exist
        if (crossFilters && Object.entries(crossFilters).length > 0) {
            finalValues = {
                ...finalValues,
                ...getCrossValues({ crossFilters, crossSchema: state.crossSchema, getValues }),
            };
        }

        return finalValues;
    }, [
        hasCrossFilters,
        state.mappedSchema,
        state.crossSchema,
        filters,
        getValues,
        entity,
        crossFilters,
        getCrossValues,
    ]);

    return {
        values,
        formatRangeValueToSave,
    };
};

export default useFilterValues;
